<template>
  <div class="home-page">
    <img
      class="page-bg"
      :src="backgroundSrc"
      alt=""
      referrerpolicy="no-referrer"
    />

    <!-- Z轴顶层：顶部导航栏 -->
    <HeaderNav class="header-layer" />
    
    <!-- Z轴下层：主要内容区域 -->
    <div class="content">
      <div class="layout">
        <!-- Z轴顶层：左侧信息栏 -->
        <aside class="sidebar sidebar-layer">
          <ProfilePanel />
        </aside>
        
        <!-- Z轴下层：主内容区域 -->
        <main class="main main-layer">
          <router-view v-slot="{ Component }">
            <transition name="fade" mode="out-in">
              <component :is="Component" />
            </transition>
          </router-view>
        </main>
      </div>
    </div>

    <footer class="site-footer">
      <p class="footer-line">
        © 2026 · Powered by
        <a
          class="author-link"
          href="https://space.bilibili.com/15278628"
          target="_blank"
          rel="noopener noreferrer"
        >白曙Shiroake</a>
      </p>
      <p class="footer-line footer-disclaimer">
        本站为爱好者作品，和官方没有关联
      </p>
    </footer>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref } from 'vue'
import axios from 'axios'
import HeaderNav from '@/components/HeaderNav.vue'
import ProfilePanel from '@/components/ProfilePanel.vue'
import apiConfig from '@/api/api.json'
import config from '@/config'
import defaultBackground from '@/assets/images/bg2.png'

const canLoadImage = (url: string) =>
  new Promise<boolean>((resolve) => {
    const img = new Image()
    img.referrerPolicy = 'no-referrer'
    img.onload = () => resolve(true)
    img.onerror = () => resolve(false)
    img.src = url
  })

export default defineComponent({
  name: 'HomePage',
  components: {
    HeaderNav,
    ProfilePanel
  },
  setup() {
    const backgroundSrc = ref(defaultBackground)

    const fetchLiveBackground = async () => {
      try {
        const { url, params, prefix } = apiConfig.bilibili.liveRoomInfo
        const { url: apiUrl, params: apiParams } = config.getApiUrl(url, params, prefix)
        const response = await axios.get(apiUrl, {
          params: apiParams,
          withCredentials: false
        })

        const remoteBg = response.data?.data?.background
        if (response.data?.code !== 0 || !remoteBg) {
          return
        }

        if (await canLoadImage(remoteBg)) {
          backgroundSrc.value = remoteBg
        }
      } catch {
        // 接口或图片失败时保持本地默认背景
      }
    }

    onMounted(() => {
      fetchLiveBackground()
    })

    return {
      backgroundSrc
    }
  }
})
</script>

<style scoped lang="scss">
.home-page {
  width: 100%;
  min-height: 100vh;
  background-color: #f6f8fa;
  position: relative;
  overflow: hidden;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;

  .page-bg {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    z-index: 0;
    pointer-events: none;
  }

  &::after {
    content: '';
    position: fixed;
    top: 50%;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      to bottom,
      rgba(246, 248, 250, 0) 0%,
      rgba(246, 248, 250, 0.8) 50%,
      rgba(246, 248, 250, 0.95) 100%
    );
    z-index: 1;
  }
}

// Z轴分层定义
.header-layer {
  position: sticky;
  top: 0;
  z-index: 100; // 顶层：导航栏
}

.sidebar-layer {
  position: sticky;
  top: 2rem;
  z-index: 90; // 顶层：侧边栏（但在导航栏下方）
  height: fit-content; // 让侧边栏高度适应内容，不占用多余空间
}

.main-layer {
  position: relative;
  z-index: 10; // 下层：主内容区域
}

.content {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 2;
  flex: 1;
}

.layout {
  display: flex;
  gap: 2rem;
  padding-top: 2rem;
  
  .sidebar {
    flex: none;
    width: 300px;
  }
  
  .main {
    flex: 1;
    min-width: 0;
  }
}

.site-footer {
  position: relative;
  z-index: 2;
  text-align: right;
  padding: 2rem 2rem 1.75rem;
  font-size: 0.8rem;
  line-height: 1.7;
  color: rgba(36, 59, 107, 0.55);
  font-family: "Microsoft YaHei", "微软雅黑", "PingFang SC", sans-serif;

  .footer-line {
    margin: 0;
  }

  .footer-disclaimer {
    margin-top: 0.15rem;
    font-size: 0.75rem;
    color: rgba(36, 59, 107, 0.42);
  }

  .author-link {
    color: rgba(45, 90, 168, 0.78);
    font-weight: 500;
    text-decoration: none;
    margin-left: 0.25em;
    transition: color 0.2s ease;

    &:hover {
      color: rgba(45, 90, 168, 0.95);
      text-decoration: underline;
    }
  }
}

// 页面切换动画
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

// 响应式布局
@media (max-width: 1024px) {
  .layout {
    flex-direction: column;
    gap: 1rem;
    
    .sidebar {
      width: 100%;
      order: 2;
    }
    
    .main {
      order: 1;
    }
  }
  
  .content {
    padding: 0 1rem;
  }
}

@media (max-width: 768px) {
  .layout {
    padding-top: 1rem;
  }
  
  .content {
    padding: 0 0.5rem;
  }
}
</style>
